import React from "react";
import { observer } from "mobx-react";
import { useNavigate } from "react-router";
import Product from "@models/product";
import { cartStore, wishlistStore } from "@stores/main";
import { generateProductDetailsUrl } from "@utils/urlUtils";
import { formatDiscountPercentage } from "@utils/formatDiscountPercentage";
import { ItemMessage } from "@components/ItemMessage";
import styles from './CartItemRedesign.module.scss';

interface CartItemRedesignProps {
  item: Product;
  setShowLoader: (show: boolean) => void;
}

const CartItemRedesign: React.FC<CartItemRedesignProps> = ({ item, setShowLoader }) => {
  const { loading } = cartStore;
  const navigate = useNavigate();

  const increaseQuantity = async (item: Product) => {
    setShowLoader(true);
    if (!loading && item.quantity < item.availableQuantity) {
      const res = await cartStore.addItemToCart(item.id);
      if (!res) {
        setTimeout(() => setShowLoader(false), 250);
        return;
      }
    } else if (!loading && item.quantity === item.availableQuantity) {
      const res = await cartStore.addItemToCart(item.id);
      if (!res) {
        setTimeout(() => setShowLoader(false), 250);
        return;
      }
    }
    setTimeout(() => setShowLoader(false), 250);
  };

  const decreaseQuantity = (item: Product) => {
    setShowLoader(true);
    if (!loading && item.quantity > 1) {
      cartStore.removeItemFromCart(item.id);
    }
    setTimeout(() => setShowLoader(false), 250);
  };

  const handleRemoveItem = (item: Product) => {
    setShowLoader(true);
    if (!loading && item.quantity >= 1) {
      cartStore.removeItemFromCart(item.id, item.quantity);
    }
    setTimeout(() => setShowLoader(false), 250);
  };

  const moveToWishlist = async (item: Product) => {
    setShowLoader(true);
    if (!loading && item.quantity > 0) {
      const ok = await wishlistStore.addProductToWishlist(item);
      if (ok) {
        cartStore.removeItemFromCart(item.id, item.quantity);
      }
    }
    setTimeout(() => setShowLoader(false), 250);
  };

  return (
    <div className={`${styles.cartItem} ${item.isSoldOut ? styles.cartItemSoldOut : ''}`}>
      <div className={styles.cartItemImage}>
        {item.discountPercentage > 0 && (
          <div className={styles.discountBadge}>
            <span className={styles.discountPercentage}>
              {formatDiscountPercentage(item.discountPercentage)}%
            </span>
            <span className={styles.discountOff}>OFF</span>
            {item.hotOffer && (
              <div className={styles.hotOfferTag}>
                <i className="fas fa-fire"></i>
              </div>
            )}
          </div>
        )}
        <img
          src={item.poster || `${process.env.PUBLIC_URL || ''}/images/product-placeholder.svg`}
          alt={item.title}
          onError={(e) => {
            const target = e.currentTarget as HTMLImageElement;
            target.onerror = null;
            target.src = `${process.env.PUBLIC_URL || ''}/images/product-placeholder.svg`;
          }}
          onClick={() => navigate(generateProductDetailsUrl(item))}
        />
        {item.isSoldOut && (
          <div className={styles.soldOutBadge}>
            OUT OF STOCK
          </div>
        )}
      </div>

      <div className={styles.cartItemDetails}>
        <div className={styles.cartItemHeader}>
          <h3 
            className={styles.cartItemTitle}
            onClick={() => navigate(generateProductDetailsUrl(item))}
          >
            {item.extendedTitle || item.title}
          </h3>
          <div className={styles.cartItemActions}>
            <button
              className={styles.actionButton}
              onClick={() => moveToWishlist(item)}
              title="Move to Wishlist"
            >
              <i className="far fa-heart"></i>
            </button>
            <button
              className={styles.actionButton}
              onClick={() => handleRemoveItem(item)}
              title="Remove Item"
            >
              <i className="fas fa-trash-alt"></i>
            </button>
          </div>
        </div>

        <div className={styles.cartItemInfo}>
          <div className={styles.storeInfo}>
            <img
              src={item.offerFromStore.icon}
              alt={item.offerFromStore.slug}
              className={styles.storeIcon}
            />
            <span className={styles.storeName}>{item.offerFromStore.text}</span>
            {item.availableRegion && (
              <span className={styles.region}>{item.availableRegion}</span>
            )}
          </div>

          <div className={styles.priceSection}>
            <span className={styles.currentPrice}>{item.buyPriceString}</span>
            {item.displayPrice > item.buyPrice && (
              <span className={styles.originalPrice}>
                {item.displayPriceString}
              </span>
            )}
          </div>

          <ItemMessage 
            itemMessage={item.itemMessage} 
            buyPrice={item.buyPrice} 
            displayPrice={item.displayPrice} 
            className={styles.itemMessage} 
          />
        </div>

        <div className={styles.quantitySection}>
          <div className={styles.quantityControls}>
            <button
              className={`${styles.quantityButton} ${styles.quantityButtonDecrease}`}
              onClick={() => decreaseQuantity(item)}
              disabled={item.quantity === 1}
            >
              <i className="fas fa-minus"></i>
            </button>
            <span className={styles.quantityValue}>{item.quantity}</span>
            <button
              className={`${styles.quantityButton} ${styles.quantityButtonIncrease}`}
              onClick={() => increaseQuantity(item)}
              disabled={item.quantity >= item.availableQuantity}
            >
              <i className="fas fa-plus"></i>
            </button>
          </div>
          <div className={styles.itemTotal}>
            <span className={styles.itemTotalPrice}>
              ₹{(item.buyPrice * item.quantity).toLocaleString('en-IN')}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default observer(CartItemRedesign);

